import DOMPurify from 'dompurify';
import { gateAnchors } from './publicRoutability.js';
import { imgUrl } from './imgUrl.js';

/**
 * Wraps DOMPurify.sanitize for use with dangerouslySetInnerHTML.
 * Usage: <div dangerouslySetInnerHTML={safeHtml(untrustedString)} />
 *
 * All <a> tags in sanitized HTML automatically receive:
 *   - target="_blank"
 *   - rel="nofollow noopener noreferrer" (external / affiliate links)
 *   - rel="noopener noreferrer"          (same-site links starting with / # ?)
 */
if (typeof DOMPurify !== 'undefined' && typeof DOMPurify.addHook === 'function') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      const href = node.getAttribute('href') || '';
      const isInternal = href.startsWith('/') || href.startsWith('#') || href.startsWith('?');
      // data-dofollow marks an intentional followed backlink (the client's own
      // website, cited in an Edit article), link equity SHOULD flow to the venue.
      // All other external links stay nofollow so LWD's equity isn't leaked broadly.
      const dofollow = node.hasAttribute('data-dofollow');
      node.setAttribute('rel', (isInternal || dofollow) ? 'noopener noreferrer' : 'nofollow noopener noreferrer');
      if (dofollow) node.removeAttribute('data-dofollow');
    }
    // Stored article HTML (magazine_blocks.body/text/content) can carry hand-pasted
    // <img> tags with raw Supabase Storage URLs baked directly into the content
    // string — not a prop any component wraps, so imgUrl() at the JSX layer never
    // sees them. This is the one choke point all of it passes through, same reason
    // the anchor gate above lives here rather than in each block renderer.
    if (node.tagName === 'IMG') {
      const src = node.getAttribute('src') || '';
      const transformed = imgUrl(src, { width: 1600 });
      if (transformed && transformed !== src) node.setAttribute('src', transformed);
    }
    // The parallax block (blockExtensions.js) bakes its image straight into an
    // inline style's background url('...') at save time, plus a parallel data-src
    // for re-editing — neither is an <img src>, so the check above never sees it.
    const style = node.getAttribute('style');
    if (style && style.includes('url(')) {
      const rewritten = style.replace(/url\((['"]?)([^'")]+)\1\)/g, (whole, quote, url) => {
        const transformed = imgUrl(url, { width: 1800 });
        return transformed && transformed !== url ? `url(${quote}${transformed}${quote})` : whole;
      });
      if (rewritten !== style) node.setAttribute('style', rewritten);
    }
    const dataSrc = node.getAttribute('data-src');
    if (dataSrc) {
      const transformed = imgUrl(dataSrc, { width: 1800 });
      if (transformed && transformed !== dataSrc) node.setAttribute('data-src', transformed);
    }
  });
}

/**
 * Sanitize, then apply the public-routability gate.
 *
 * Every editorial body block renders through this one function, which makes it
 * the only choke point where stored HTML meets the page. That is why the gate
 * lives here rather than in each block renderer: an internal-link audit on
 * 2026-08-15 found 117 dead internal links, almost all of them hardcoded hrefs
 * inside stored article HTML pointing at draft venues, private venues, draft
 * showcases, held destinations and retired categories.
 *
 * gateAnchors unwraps those anchors to plain text, so the words survive and the
 * broken link does not. It runs AFTER sanitize so it only ever sees markup
 * DOMPurify already approved, and it is a no-op without a DOM, so any
 * server-side caller degrades to the previous behaviour instead of throwing.
 */
export const safeHtml = (dirty) => ({
  __html: gateAnchors(DOMPurify.sanitize(dirty ?? '')),
});
